class MasiqHeader extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.mobileMenu = null;
    this.mobileSummary = null;
    this.mobilePanel = null;
    this.previouslyFocused = null;
    this.resizeObserver = null;
    this.inertedBackgroundElements = [];
    this.mobileQuery = window.matchMedia('(max-width: 1099px)');
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.mobileMenu = this.querySelector('[data-mobile-menu]');
    this.mobileSummary = this.mobileMenu?.querySelector(':scope > summary');
    this.mobilePanel = this.querySelector('[data-menu-panel]');

    if (this.dataset.sticky === 'true') {
      this.parentElement?.classList.add('masiq-header-section--sticky');
    }

    this.mobileMenu?.addEventListener('toggle', this.handleMobileToggle, { signal });
    this.querySelectorAll('[data-menu-close]').forEach((button) => {
      button.addEventListener('click', this.closeMobileMenu, { signal });
    });
    this.querySelectorAll('[data-menu-panel] a').forEach((link) => {
      link.addEventListener('click', this.closeMobileMenu, { signal });
    });

    this.querySelectorAll('[data-desktop-dropdown]').forEach((dropdown) => {
      dropdown.addEventListener('toggle', this.handleDesktopToggle, { signal });
    });

    document.addEventListener('keydown', this.handleKeydown, { signal });
    document.addEventListener('pointerdown', this.handleOutsidePointer, { signal });
    this.mobileQuery.addEventListener('change', this.handleBreakpointChange, { signal });

    const headerGroup = document.getElementById('header-group');
    this.resizeObserver = new ResizeObserver(this.updateHeaderMetrics);
    this.resizeObserver.observe(this);
    if (headerGroup) this.resizeObserver.observe(headerGroup);
    requestAnimationFrame(this.updateHeaderMetrics);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.restoreBackgroundInteractivity();
    this.parentElement?.classList.remove('masiq-header-section--sticky');
    document.documentElement.classList.remove('masiq-menu-open');
  }

  updateHeaderMetrics = () => {
    const headerGroup = document.getElementById('header-group');
    const headerHeight = Math.round(this.getBoundingClientRect().height);
    const groupHeight = Math.round(headerGroup?.getBoundingClientRect().height || headerHeight);

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    document.body.style.setProperty('--top-row-height', `${headerHeight}px`);
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
  };

  handleMobileToggle = () => {
    if (!this.mobileMenu || !this.mobileSummary) return;

    const isOpen = this.mobileMenu.open;
    this.mobileSummary.setAttribute('aria-expanded', String(isOpen));
    this.mobileSummary.setAttribute('aria-label', isOpen ? 'Menu sluiten' : 'Menu openen');
    document.documentElement.classList.toggle('masiq-menu-open', isOpen);
    this.setBackgroundInert(isOpen);

    if (isOpen) {
      this.previouslyFocused = document.activeElement;
      requestAnimationFrame(() => {
        this.mobilePanel?.querySelector('[data-menu-close]:not([tabindex="-1"])')?.focus();
      });
    } else {
      this.querySelectorAll('.masiq-header__mobile-submenu[open]').forEach((submenu) => {
        submenu.open = false;
      });

      const menuShell = this.querySelector('[data-menu-shell]');
      if (menuShell?.contains(document.activeElement)) {
        const focusTarget = !this.mobileQuery.matches
          ? this.getDesktopFocusFallback()
          : this.previouslyFocused instanceof HTMLElement && document.contains(this.previouslyFocused)
            ? this.previouslyFocused
            : this.mobileSummary;
        focusTarget?.focus();
      }
      this.previouslyFocused = null;
    }
  };

  closeMobileMenu = () => {
    if (this.mobileMenu?.open) this.mobileMenu.open = false;
  };

  setBackgroundInert(isInert) {
    if (!isInert) {
      this.restoreBackgroundInteractivity();
      return;
    }

    this.restoreBackgroundInteractivity();

    let activeBranch = this;
    while (activeBranch && activeBranch !== document.body) {
      const parent = activeBranch.parentElement;
      if (!parent) break;

      Array.from(parent.children).forEach((sibling) => {
        if (
          sibling === activeBranch ||
          !(sibling instanceof HTMLElement) ||
          ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(sibling.tagName) ||
          sibling.hasAttribute('inert')
        ) {
          return;
        }

        sibling.setAttribute('inert', '');
        this.inertedBackgroundElements.push(sibling);
      });

      activeBranch = parent;
    }
  }

  restoreBackgroundInteractivity() {
    this.inertedBackgroundElements.forEach((element) => {
      if (element instanceof HTMLElement) element.removeAttribute('inert');
    });
    this.inertedBackgroundElements = [];
  }

  handleDesktopToggle = (event) => {
    const openedDropdown = event.currentTarget;
    if (!(openedDropdown instanceof HTMLDetailsElement) || !openedDropdown.open) return;

    this.querySelectorAll('[data-desktop-dropdown][open]').forEach((dropdown) => {
      if (dropdown !== openedDropdown) dropdown.open = false;
    });
  };

  handleOutsidePointer = (event) => {
    if (!(event.target instanceof Node)) return;

    this.querySelectorAll('[data-desktop-dropdown][open]').forEach((dropdown) => {
      if (!dropdown.contains(event.target)) dropdown.open = false;
    });
  };

  handleBreakpointChange = () => {
    const wasMobileMenuOpen = Boolean(this.mobileMenu?.open);

    if (!this.mobileQuery.matches) {
      this.closeMobileMenu();

      if (wasMobileMenuOpen) {
        requestAnimationFrame(() => {
          this.getDesktopFocusFallback()?.focus({ preventScroll: true });
        });
      }
    }

    this.closeDesktopDropdowns();
    requestAnimationFrame(this.updateHeaderMetrics);
  };

  getDesktopFocusFallback() {
    const candidates = [
      this.querySelector('.masiq-header__brand'),
      this.querySelector('.masiq-header__actions a[href]'),
      this.querySelector('.masiq-header__actions button:not([disabled])'),
    ];

    return candidates.find(
      (candidate) => candidate instanceof HTMLElement && candidate.getClientRects().length > 0
    );
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      if (this.mobileMenu?.open) {
        event.preventDefault();
        this.closeMobileMenu();
        return;
      }

      const openDropdown = this.querySelector('[data-desktop-dropdown][open]');
      if (openDropdown instanceof HTMLDetailsElement) {
        event.preventDefault();
        openDropdown.open = false;
        openDropdown.querySelector(':scope > summary')?.focus();
      }
      return;
    }

    if (event.key !== 'Tab' || !this.mobileMenu?.open || !this.mobilePanel) return;

    const focusable = Array.from(
      this.mobilePanel.querySelectorAll(
        'a[href], button:not([disabled]):not([tabindex="-1"]), summary, select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  closeDesktopDropdowns() {
    this.querySelectorAll('[data-desktop-dropdown][open]').forEach((dropdown) => {
      dropdown.open = false;
    });
  }
}

if (!customElements.get('masiq-header')) {
  customElements.define('masiq-header', MasiqHeader);
}
